/* eslint-disable camelcase */
import React, { useState } from "react"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material"
import {
  AccountBalanceWallet,
  CheckCircle,
  PhoneInTalk,
  Speed,
  TrendingUp,
  Dashboard as DashboardIcon,
} from "@mui/icons-material"
import { PieChart } from "@mui/x-charts/PieChart"
import { BarChart } from "@mui/x-charts/BarChart"
import dayjs from "dayjs"

// Importaciones estándar de su arquitectura SIAC
import Header from "../../layouts/Header"
import BackIcon from "../../components/BackIcon"
import CustomBackdrop from "../../components/CustomBackdrop"
import CustomDatePicker from "../../components/CustomDatePicker"
import { useQuery, api } from "../../api"

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(3),
  flexGrow: 1,
  padding: "0 16px",
  paddingBottom: "40px",
}))

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

// Utilidad para formatear moneda
const formatMoneda = (valor) => {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(valor || 0)
}

// --- COMPONENTE REUTILIZABLE PARA LAS TARJETAS KPI ---
const KPICard = ({ title, value, icon, color, subtitle }) => (
  <Card elevation={2} sx={{ height: "100%", borderLeft: `6px solid ${color}`, borderRadius: 2 }}>
    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography
            variant="body2"
            color="textSecondary"
            fontWeight="bold"
            sx={{ textTransform: "uppercase", mb: 1 }}
          >
            {title}
          </Typography>
          <Typography variant="h4" fontWeight="bold" sx={{ color }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: "block" }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ bgcolor: `${color}1A`, p: 1.5, borderRadius: "50%", display: "flex" }}>
          {React.cloneElement(icon, { sx: { color, fontSize: 32 } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
)

const DashboardCarteraIndex = () => {
  // Filtros: Por defecto evaluamos los últimos 30 días hasta hoy
  const [fecIni, setFecIni] = useState(dayjs().subtract(30, "day"))
  const [fecFin, setFecFin] = useState(dayjs())
  const [recaudadorFiltro, setRecaudadorFiltro] = useState("TODOS")

  // 1. OBTENER CATÁLOGO DE OFICIALES / RECAUDADORES
  const { data: recaudadores = [], isLoading: loadingOfi } = useQuery({
    queryKey: ["getRecaudadoresDashboardCartera"],
    queryFn: async () => {
      try {
        const payload = {
          page: 1,
          perPage: 1000,
          filters: { usrstatus: "A", usrflagoficre: -1 },
        }
        const res = await api.post("/CreacionUsuarios/getAllUsuarios", payload)

        // Buscamos dinámicamente el arreglo de datos sin importar la anidación del backend
        let dataArray = []
        if (Array.isArray(res.data)) dataArray = res.data
        else if (Array.isArray(res.data?.data)) dataArray = res.data.data
        else if (Array.isArray(res.data?.data?.data)) dataArray = res.data.data.data

        return dataArray
      } catch (error) {
        console.error("Error al cargar oficiales:", error)
        return [] // En caso de error, devolvemos un arreglo vacío para no romper la pantalla
      }
    },
    refetchOnWindowFocus: false,
  })

  // 2. OBTENER MÉTRICAS DEL NUEVO DASHBOARD DE EFECTIVIDAD
  const { data: metrics, isLoading: loadingMetrics } = useQuery({
    // El query se re-ejecuta automáticamente si cambian las fechas o el recaudador
    queryKey: ["getDashboardCobranzas", fecIni, fecFin, recaudadorFiltro],
    queryFn: async () => {
      const payload = {
        filtros: {
          fecIni: fecIni ? dayjs(fecIni).format("YYYY-MM-DD") : "",
          fecFin: fecFin ? dayjs(fecFin).format("YYYY-MM-DD") : "",
          recaudador: recaudadorFiltro === "TODOS" ? "" : recaudadorFiltro,
        },
      }
      const res = await api.post("/AsignaciondeCartera/getDashboardCobranzas", payload)
      return res.data
    },
    refetchOnWindowFocus: false,
    enabled: !!fecIni && !!fecFin, // Solo busca si hay fechas válidas
  })

  const isLoading = loadingOfi || loadingMetrics

  // Extraemos la data de forma segura (Fallback si viene vacío)
  const kpis = metrics?.kpis || {
    total_asignado: 0,
    total_recuperado: 0,
    saldo_pendiente: 0,
    efectividad_pct: 0,
    hit_rate_pct: 0,
    velocidad_cierre_dias: 0,
    total_facturas_asignadas: 0,
  }
  const agingData = metrics?.charts?.aging || []
  const leaderboard = metrics?.leaderboard || []

  // Preparar data para Gráfico de Pastel (Embudo de Dinero)
  const pieData = [
    { id: 0, value: kpis.total_recuperado, label: "Recuperado (Éxito)", color: "#2E7D32" },
    { id: 1, value: kpis.saldo_pendiente, label: "Pendiente (Riesgo)", color: "#d32f2f" },
  ]

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        {/* TÍTULO */}
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mb: 1, color: "#196C87" }}>
          <DashboardIcon sx={{ fontSize: 36, mr: 1 }} />
          <Typography variant="h4" fontWeight="bold">
            Efectividad de Cobranzas
          </Typography>
        </Box>
        <Typography variant="body1" align="center" color="textSecondary" mb={4}>
          Evaluación de rendimiento del equipo de recaudación y análisis de recuperación de cartera
        </Typography>

        <CustomBackdrop isLoading={isLoading} />

        <StyledRoot>
          {/* PANEL DE FILTROS */}
          <Paper elevation={1} sx={{ p: 2, mb: 4, borderRadius: 2, borderTop: "4px solid #196C87" }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4} md={3}>
                <CustomDatePicker label="Asignado Desde" value={fecIni} setValue={setFecIni} format="DD/MM/YYYY" />
              </Grid>

              <Grid item xs={12} sm={4} md={3}>
                <CustomDatePicker label="Asignado Hasta" value={fecFin} setValue={setFecFin} format="DD/MM/YYYY" />
              </Grid>

              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  select
                  fullWidth
                  label="Oficial / Recaudador"
                  value={recaudadorFiltro}
                  onChange={(e) => setRecaudadorFiltro(e.target.value)}
                  size="small"
                >
                  <MenuItem value="TODOS">Todos los Recaudadores</MenuItem>
                  {recaudadores.map((rec) => (
                    <MenuItem key={rec.usrcodigo} value={rec.usrcodigo}>
                      {rec.usrnombre} ({rec.usrcodigo})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="textSecondary" align="right">
                  Última evaluación: {dayjs().format("DD/MM/YYYY HH:mm")}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* SECCIÓN DE MÉTRICAS PRINCIPALES (KPIs) */}
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} sm={6} md={3}>
              <KPICard
                title="Cartera Asignada"
                value={formatMoneda(kpis.total_asignado)}
                icon={<AccountBalanceWallet />}
                color="#196C87"
                subtitle={`${kpis.total_facturas_asignadas} facturas en gestión`}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <KPICard
                title="Efectividad (Recuperado)"
                value={`${kpis.efectividad_pct}%`}
                icon={<TrendingUp />}
                color={kpis.efectividad_pct >= 50 ? "#2E7D32" : "#ed6c02"}
                subtitle={formatMoneda(kpis.total_recuperado)}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <KPICard
                title="Tasa de Contacto (Hit Rate)"
                value={`${kpis.hit_rate_pct}%`}
                icon={<PhoneInTalk />}
                color="#0288d1"
                subtitle="Cuentas con al menos 1 abono"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <KPICard
                title="Velocidad de Cierre"
                value={`${kpis.velocidad_cierre_dias} Días`}
                icon={<Speed />}
                color="#9c27b0"
                subtitle="Promedio para liquidar factura"
              />
            </Grid>
          </Grid>

          <Divider sx={{ mb: 4 }} />

          {/* SECCIÓN DE GRÁFICOS Y TABLAS */}
          <Grid container spacing={3}>
            {/* Gráfico 1: Embudo de Dinero (Dona) */}
            <Grid item xs={12} md={4}>
              <Paper
                elevation={2}
                sx={{ p: 3, borderRadius: 2, height: "100%", display: "flex", flexDirection: "column" }}
              >
                <Typography variant="h6" color="primary" fontWeight="bold" gutterBottom align="center">
                  Embudo de Recuperación
                </Typography>
                <Box
                  sx={{ flexGrow: 1, display: "flex", justifyContent: "center", alignItems: "center", minHeight: 250 }}
                >
                  {kpis.total_asignado === 0 ? (
                    <Typography color="textSecondary">Sin datos en el período</Typography>
                  ) : (
                    <PieChart
                      series={[
                        {
                          data: pieData,
                          highlightScope: { faded: "global", highlighted: "item" },
                          faded: { innerRadius: 30, additionalRadius: -30, color: "gray" },
                          innerRadius: 60,
                          paddingAngle: 2,
                          cornerRadius: 4,
                          valueFormatter: (v) => formatMoneda(v.value),
                        },
                      ]}
                      height={250}
                      slotProps={{
                        legend: { direction: "row", position: { vertical: "bottom", horizontal: "middle" } },
                      }}
                    />
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Gráfico 2: Desempeño por Antigüedad de la Deuda (Aging) */}
            <Grid item xs={12} md={8}>
              <Paper
                elevation={2}
                sx={{ p: 3, borderRadius: 2, height: "100%", display: "flex", flexDirection: "column" }}
              >
                <Typography variant="h6" color="primary" fontWeight="bold" gutterBottom align="center">
                  Gestión por Antigüedad de la Deuda (Aging)
                </Typography>
                <Box
                  sx={{ flexGrow: 1, display: "flex", justifyContent: "center", alignItems: "center", minHeight: 250 }}
                >
                  {agingData.length === 0 ? (
                    <Typography color="textSecondary">Sin datos en el período</Typography>
                  ) : (
                    <BarChart
                      xAxis={[{ scaleType: "band", data: agingData.map((d) => d.rango) }]}
                      series={[
                        {
                          data: agingData.map((d) => d.recuperado),
                          label: "Recuperado",
                          color: "#2E7D32",
                          valueFormatter: (v) => formatMoneda(v),
                        },
                        {
                          data: agingData.map((d) => d.pendiente),
                          label: "Pendiente",
                          color: "#d32f2f",
                          valueFormatter: (v) => formatMoneda(v),
                        },
                      ]}
                      height={300}
                      margin={{ top: 20, bottom: 40, left: 70, right: 20 }}
                    />
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Tabla 3: Ranking de Recaudadores (Leaderboard) */}
            <Grid item xs={12}>
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" color="primary" fontWeight="bold" gutterBottom>
                  Ranking de Efectividad (Leaderboard)
                </Typography>
                <TableContainer>
                  <Table size="small" sx={{ minWidth: 650 }}>
                    <TableHead sx={{ backgroundColor: "#f5f7fa" }}>
                      <TableRow>
                        <TableCell>
                          <b>Posición</b>
                        </TableCell>
                        <TableCell>
                          <b>Recaudador / Oficial</b>
                        </TableCell>
                        <TableCell align="right">
                          <b>Asignado (USD)</b>
                        </TableCell>
                        <TableCell align="right">
                          <b>Recuperado (USD)</b>
                        </TableCell>
                        <TableCell align="center">
                          <b>Hit Rate (%)</b>
                        </TableCell>
                        <TableCell align="center">
                          <b>Efectividad (%)</b>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {leaderboard.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            No hay gestiones en el rango seleccionado.
                          </TableCell>
                        </TableRow>
                      ) : (
                        leaderboard.map((row, index) => (
                          <TableRow key={row.recaudador} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                            <TableCell>
                              {index === 0 ? "🥇 1" : index === 1 ? "🥈 2" : index === 2 ? "🥉 3" : index + 1}
                            </TableCell>
                            <TableCell>{row.recaudador}</TableCell>
                            <TableCell align="right">{formatMoneda(row.asignado)}</TableCell>
                            <TableCell align="right" sx={{ color: "#2E7D32", fontWeight: "bold" }}>
                              {formatMoneda(row.recuperado)}
                            </TableCell>
                            <TableCell align="center">{row.hit_rate_pct}%</TableCell>
                            <TableCell align="center">
                              <Box
                                sx={{
                                  bgcolor: row.efectividad_pct >= 50 ? "#e8f5e9" : "#fff3e0",
                                  color: row.efectividad_pct >= 50 ? "#2e7d32" : "#e65100",
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  fontWeight: "bold",
                                  display: "inline-block",
                                }}
                              >
                                {row.efectividad_pct}%
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>
        </StyledRoot>
      </div>
    </ThemeProvider>
  )
}

export default DashboardCarteraIndex
